/**
 * Reports.
 *
 * Seven reports, all reading the same signal engine as the department cards. They differ
 * in what is shown, never in how anything is calculated — so if two reports disagree, that
 * is a bug, not a difference of method.
 *
 * One deliberate exception is worth knowing before comparing totals: the Executive Summary
 * *report* here counts every department regardless of the Validation Gate, while the
 * Executive Summary *tab* filters to validated ones. Same name, two views, built at
 * different times to answer different questions.
 */
import { useMemo, useState } from 'react';
import { useModel, useDepartmentSignals } from '../state.jsx';
import { Icon } from '../components/IconSprite.jsx';

const REPORTS = [
  ['executive', 'Executive Summary', 'Org-wide health, alerts, key metrics', 'ic-bars'],
  ['capacity', 'Capacity Status', 'Live signal & FTE gap per department', 'ic-target'],
  ['alerts', 'Hiring Alerts', 'Active alerts and time-to-alert', 'ic-alert-tri'],
  ['coverage', 'Coverage Risk Report', 'Absence exposure across all staff', 'ic-shield'],
  ['fragility', 'Team Fragility Report', 'Seniority risk and fragility scores', 'ic-pulse'],
  ['roster', 'Team Roster Summary', 'Headcount, seniority mix, vacancies', 'ic-users'],
  ['pipeline', 'Demand Pipeline', 'Anticipated demand and active projects', 'ic-trend'],
];

const MODEL_LABEL = {
  volume: ['Volume', '📦'], coverage: ['Coverage', '🛡️'], support: ['Support', '🤝'],
  judgment: ['Judgment', '🎯'], notConfigured: ['Not Configured', '⚙️'], dataInvalid: ['Data Invalid', '🛑'],
};

function statusCell(signal) {
  if (signal.dataInvalid) return { icon: '🛑', label: 'Data Invalid', color: 'var(--purple)' };
  if (signal.notConfigured) return { icon: '⚙️', label: 'Not Configured', color: 'var(--ink4)' };
  if (signal.flagged) return { icon: '🚨', label: 'Alert Active', color: 'var(--red)' };
  if (signal.watch) return { icon: '⚠️', label: 'Monitoring', color: 'var(--amber)' };
  if (signal.kind === 'judgment') return { icon: 'ℹ️', label: 'Informational', color: 'var(--blue)' };
  return { icon: '✅', label: 'Within Threshold', color: 'var(--grn)' };
}

/** The department's own signal, in its own units — never a derived cross-type score. */
function signalText(dep, signal) {
  if (signal.dataInvalid) return signal.invalidReasons.join('; ');
  if (signal.notConfigured) return 'No roles or roster loaded';
  if (signal.kind === 'volume') return `${signal.impliedFTE} FTE implied`;
  if (signal.kind === 'coverage') return `${signal.covUncovered} uncovered · ${signal.covSingle} single-point`;
  if (signal.kind === 'support') return `${signal.hcRatioActual}:1`;
  return 'No formula';
}

/** Governance items are tracked independently of status. */
function governanceFor(dep, signal) {
  const items = [];
  if (signal.kind === 'volume' && dep.volDrivers?.length > 1 && !dep.volCombineLocked) items.push('Methodology Pending Approval');
  if ((dep.volPeriodBasis ?? 'current') !== 'current' && !dep.volPeriodBasisLocked) items.push('Period Basis Pending Approval');
  return items;
}

const toCsv = (rows) =>
  rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');

export function Reports() {
  const { state, engine } = useModel();
  const signals = useDepartmentSignals();
  const [active, setActive] = useState('executive');
  const [scope, setScope] = useState('ALL');
  const [period, setPeriod] = useState(`Q${Math.floor(new Date().getMonth() / 3) + 1} ${new Date().getFullYear()}`);

  const scoped = useMemo(
    () => signals.filter(({ dep }) => scope === 'ALL' || dep.id === scope),
    [signals, scope],
  );

  const meta = REPORTS.find(([key]) => key === active);

  const report = useMemo(() => {
    if (!state || !engine) return null;
    const count = (fn) => scoped.filter(fn).length;

    if (active === 'executive' || active === 'capacity') {
      return {
        kpis: active === 'executive' ? [
          ['Total Active Staff', state.team.filter((e) => engine.isActiveRosterRecord(e) && !e.vacant).length, `Across ${state.depts.length} departments`, 'teal'],
          ['Flagged FTE Gap', scoped.reduce((s, { signal }) => s + (signal.kind === 'volume' && signal.flagged ? signal.volGap : 0), 0).toFixed(2), 'Productive FTE short across flagged volume-threshold departments', 'red'],
          ['Departments Flagged', count(({ signal }) => signal.flagged), 'Require attention', 'red'],
          ['Open Vacancies', state.team.filter((e) => e.vacant && !e._builtStale).length, 'Unfilled funded positions', 'amber'],
          ['Data Invalid', count(({ signal }) => signal.dataInvalid), 'No departments blocked by bad data', 'green'],
          ['Not Configured', count(({ signal }) => signal.notConfigured), 'Missing roles/roster — excluded from all counts above', 'grey'],
        ] : null,
        sectionTitle: 'Department Health Snapshot',
        sectionNote: 'Signal reflects each department’s own model (volume, coverage, staffing ratio, or manager judgment) — not one hours-based number for everyone.',
        head: ['Department', 'Staff', 'Model', 'Signal', 'Status'],
        rows: scoped.map(({ dep, signal }) => {
          const st = statusCell(signal);
          const [modelLabel, modelIcon] = MODEL_LABEL[signal.kind] ?? ['—', ''];
          const gov = governanceFor(dep, signal);
          return {
            key: dep.id,
            cells: [
              <><span style={{ width: 7, height: 7, borderRadius: '50%', background: dep.color, display: 'inline-block', marginRight: 7 }} /><strong>{dep.name}</strong></>,
              state.team.filter((e) => e.dept === dep.id && !e._builtStale).length,
              <>{modelIcon} {modelLabel}</>,
              <span style={{ color: st.color, fontWeight: 600 }}>{signalText(dep, signal)}</span>,
              <>
                <span style={{ color: st.color, fontWeight: 600 }}>{st.icon} {st.label}</span>
                {gov.map((g) => (
                  <span key={g} style={{ color: 'var(--amber)', fontWeight: 600, marginLeft: 8 }}>⚠️ {g}</span>
                ))}
              </>,
            ],
            csv: [dep.name, state.team.filter((e) => e.dept === dep.id && !e._builtStale).length, modelLabel, signalText(dep, signal), st.label],
          };
        }),
      };
    }

    if (active === 'alerts') {
      // Flagged, Watch and Data Invalid, plus otherwise-healthy departments carrying an
      // unresolved governance item. Healthy departments with none of those are correctly
      // excluded, as are Not Configured ones.
      const rows = scoped
        .filter(({ dep, signal }) =>
          signal.flagged || signal.watch || signal.dataInvalid ||
          (!signal.notConfigured && governanceFor(dep, signal).length > 0))
        .map(({ dep, signal }) => {
          const st = statusCell(signal);
          const gov = governanceFor(dep, signal);
          return {
            key: dep.id,
            cells: [
              <strong>{dep.name}</strong>,
              <span style={{ color: st.color, fontWeight: 600 }}>{st.icon} {st.label}</span>,
              gov.length ? gov.join(', ') : '—',
              <span className={`chip ${dep.approvedForReporting ? 'chip-green' : 'chip-grey'}`}>
                {dep.approvedForReporting ? 'Validated' : 'Not yet'}
              </span>,
            ],
            csv: [dep.name, st.label, gov.join('; ') || '—', dep.approvedForReporting ? 'Validated' : 'Not yet'],
          };
        });
      return {
        sectionTitle: 'Active Alerts',
        sectionNote: 'Includes otherwise-healthy departments carrying an unresolved governance item — those do not show up as a status colour anywhere else. The Reporting Scope column separates “needs attention from whoever is configuring this” from “validated finding”.',
        head: ['Department', 'Status', 'Governance', 'Reporting Scope'], rows,
      };
    }

    if (active === 'coverage') {
      return {
        sectionTitle: 'Desk Coverage',
        head: ['Desk', 'Uncovered blocks', 'Single-point blocks', 'Status'],
        rows: scoped.filter(({ signal }) => signal.kind === 'coverage').map(({ dep, signal }) => {
          const st = statusCell(signal);
          return {
            key: dep.id,
            cells: [<strong>{dep.name}</strong>, signal.covUncovered, signal.covSingle,
              <span style={{ color: st.color, fontWeight: 600 }}>{st.icon} {st.label}</span>],
            csv: [dep.name, signal.covUncovered, signal.covSingle, st.label],
          };
        }),
      };
    }

    if (active === 'fragility') {
      return {
        sectionTitle: 'Fragility by Department',
        sectionNote: 'Runs on the legacy hours-based capacity calculation, not the live volume/coverage/support model — directional only.',
        head: ['Department', 'Score', 'Capacity at risk', 'Seniority mix'],
        rows: state.depts
          .filter((dep) => scope === 'ALL' || dep.id === scope)
          .map((dep) => ({ dep, f: engine.calcFragility(dep.id) }))
          .filter(({ f }) => f && f.score != null && !Number.isNaN(f.score))
          .sort((a, b) => b.f.score - a.f.score)
          .map(({ dep, f }) => {
            const mix = Object.entries(f.tiers ?? {}).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(' · ');
            const color = f.score >= 60 ? 'var(--red)' : f.score >= 35 ? 'var(--amber)' : 'var(--grn)';
            return {
              key: dep.id,
              cells: [<strong>{dep.name}</strong>,
                <span className="num-mono" style={{ color, fontWeight: 600 }}>{f.score}</span>,
                <span className="num-mono">{f.riskCapPct}%</span>, mix],
              csv: [dep.name, f.score, `${f.riskCapPct}%`, mix],
            };
          }),
      };
    }

    if (active === 'roster') {
      return {
        sectionTitle: 'Roster by Department',
        head: ['Department', 'Filled', 'Vacant', 'Recruitable', 'Ramping'],
        rows: state.depts
          .filter((dep) => scope === 'ALL' || dep.id === scope)
          .map((dep) => {
            const team = state.team.filter((e) => e.dept === dep.id && !e._builtStale);
            const cells = [
              team.filter((e) => !e.vacant).length,
              team.filter((e) => e.vacant).length,
              team.filter((e) => engine.isVacancyRecruitable(e)).length,
              team.filter((e) => e.rampWeeksRemaining > 0).length,
            ];
            return { key: dep.id, cells: [<strong>{dep.name}</strong>, ...cells], csv: [dep.name, ...cells], any: cells.some((n) => n > 0) };
          })
          .filter((r) => r.any),
      };
    }

    const deptName = (id) => state.depts.find((d) => d.id === id)?.name ?? id;
    return {
      sectionTitle: 'Anticipated Demand',
      sectionNote: 'Forward-looking only — never folded into any department’s current status or gap.',
      head: ['Prospect', 'Department', 'Service hrs/wk', 'Probability', 'Closes (wk)', 'Weighted hrs'],
      rows: state.pipeline
        .filter((p) => scope === 'ALL' || p.dept === scope)
        .map((p) => {
          const cells = [p.name, deptName(p.dept), p.svcHrs, p.prob, p.closeWk, Math.round((p.svcHrs ?? 0) * (p.prob ?? 1))];
          return { key: p.id, cells: [<strong>{cells[0]}</strong>, ...cells.slice(1)], csv: cells };
        }),
    };
  }, [active, state, engine, scoped, scope]);

  if (!report) return null;

  function download() {
    const rows = [report.head, ...report.rows.map((r) => r.csv)];
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `capacitea-${active}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const scopeLabel = scope === 'ALL' ? 'All Departments' : state.depts.find((d) => d.id === scope)?.name;

  return (
    <div className="page">
      <div className="page-hdr">
        <div className="ph-row">
          <div className="ph-icon"><Icon name="ic-file" /></div>
          <div>
            <div className="page-hdr-title">Reports</div>
            <div className="page-hdr-sub">Generate, preview, and export reports from your current model data</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20, alignItems: 'start' }}>
        <div>
          <div className="card">
            <div className="card-hdr dark">Select report</div>
            <div className="card-body flush" style={{ padding: 8 }}>
              {REPORTS.map(([key, label, sub]) => (
                <div
                  key={key}
                  onClick={() => setActive(key)}
                  style={{
                    padding: '9px 12px', borderRadius: 'var(--rsm)', cursor: 'pointer',
                    background: active === key ? 'var(--teal3)' : 'transparent',
                    border: active === key ? '1px solid var(--teal)' : '1px solid transparent',
                    marginBottom: 3,
                  }}
                >
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: active === key ? 'var(--teal2)' : 'var(--ink)' }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--ink4)', marginTop: 1 }}>{sub}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-hdr grey">Options</div>
            <div className="card-body">
              <div className="config-section-label" style={{ padding: 0, marginBottom: 4 }}>Department scope</div>
              <select className="ei" value={scope} onChange={(e) => setScope(e.target.value)}
                style={{ width: '100%', border: '1.5px solid var(--border)', borderRadius: 'var(--rsm)', padding: '6px 8px', marginBottom: 12 }}>
                <option value="ALL">All Departments</option>
                {state.depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>

              <div className="config-section-label" style={{ padding: 0, marginBottom: 4 }}>Date / period label</div>
              <input className="ei" value={period} onChange={(e) => setPeriod(e.target.value)}
                style={{ width: '100%', border: '1.5px solid var(--border)', borderRadius: 'var(--rsm)', padding: '6px 8px', marginBottom: 12 }} />

              <button className="btn btn-primary" style={{ width: '100%', marginBottom: 6 }} onClick={download}>
                ⬇ Export CSV
              </button>
              <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => window.print()}>
                🖨 Print / Save as PDF
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-hdr dark" style={{ alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, textTransform: 'none', letterSpacing: '-.2px' }}>
                {meta[1]}
              </div>
              <div style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', opacity: .7, marginTop: 3 }}>
                {state.branding.org} · CapaciTEA v20 · {period} · Scope: {scopeLabel}
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 10, opacity: .7 }}>
              Generated
              <div style={{ fontSize: 12, fontWeight: 600, opacity: 1 }}>
                {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>
          </div>

          <div className="card-body">
            {report.kpis && (
              <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}>
                {report.kpis.map(([label, value, sub, tone]) => (
                  <div key={label} className={`kpi ${tone === 'grey' ? '' : tone}`}>
                    <div className="kpi-label">{label}</div>
                    <div className="kpi-value">{value}</div>
                    <div className="kpi-sub">{sub}</div>
                  </div>
                ))}
              </div>
            )}

            {report.sectionTitle && (
              <div className="config-section-label" style={{ padding: '4px 0' }}>{report.sectionTitle}</div>
            )}
            {report.sectionNote && (
              <div style={{ fontSize: 11, color: 'var(--ink4)', marginBottom: 10, lineHeight: 1.6 }}>
                {report.sectionNote}
              </div>
            )}

            <div className="table-scroll">
              <table className="tbl">
                <thead><tr>{report.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  {report.rows.map((row) => (
                    <tr key={row.key}>
                      {row.cells.map((cell, i) => <td key={i}>{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
